/*
 * THE COMPOSER'S WORDS AND RULES (frames 1d, 1h, 1i), pure, for ReplyPanel.js.
 *
 * ReplyPanel is one component used in four places (the record, Today's rows,
 * Follow-ups, the calendar), so everything it decides that is not React lives
 * here, where test/record-composer-model.test.js can pin it without a browser:
 *
 *   - what it is answering: "Replying to Ella, Sep 30: Two redlines…", read off
 *     the company timeline the record already holds (replyQuote);
 *   - what it is called when it answers nothing ("Welcome email to Shahar") and
 *     how the draft was made ("drafted", "your standard email", "· edited");
 *   - the offered times as chips in THEIR zone ("Tue Oct 6 · 10:00 ET"), none at
 *     all when we do not know where they are, and which of them the body already
 *     names, so a chip is tinted only while its time is really in the email;
 *   - the booking link put into the starting draft (the first email, Re-engage);
 *   - the confirm's question ("Send this to Ella?") and where it goes;
 *   - an error as a sentence that says the draft is safe, worded once;
 *   - the unsent-draft registry: what lets the panel around a composer ask it
 *     before closing, rather than unmounting an edited draft without a word.
 *
 * No em dashes in anything here that reaches the screen or an email: the house
 * style, and the drafted emails' own rule.
 */
import { latestFormSubmission, formMessage } from "../formAnswers.mjs";
import { cleanBodyFolded } from "../timelineModel.mjs";
import { firstLine, whoLine, peopleOf, isCalendarMail } from "./timelineModel.mjs";
import { bothZones } from "./zones.mjs";
import { daysBetween, shortDate, toDate } from "./format.mjs";

/* ------------------------------------------------------------ names and titles */

/** "Ella Moss" → "Ella"; an address stays whole; null stays null. */
export function firstWord(name) {
  const s = typeof name === "string" ? name.trim() : "";
  if (!s) return null;
  if (s.includes("@")) return s;
  return s.split(/\s+/)[0];
}

// Answers versus new messages. A reply kind quotes what it answers; the rest
// start something (a first email, a welcome) and are titled instead.
const REPLY_KINDS = new Set(["reply_waiting", "agreement_reply"]);
// The chases: each follows up whatever was said last. A call follow-up and a
// no-show rescue are about a call, not an email, and say so in their own note
// ("From the call …"), so they quote nothing.
const FOLLOW_KINDS = new Set(["nudge_due", "final_check_due", "pricing_stale", "agreement_stale", "wake_up_due", "re_engage"]);
const CALL_KINDS = new Set(["call_followup", "no_show_rescue"]);
// The kinds that start a conversation on purpose: no thread is not news for them.
export const FRESH_KINDS = new Set(["first_touch", "kickoff", "finance_intro"]);

const TITLES = {
  first_touch: (n) => `First email to ${n}`,
  kickoff: (n) => `Welcome email to ${n}`,
  finance_intro: (n) => `Finance intro to ${n}`,
  reply_waiting: (n) => `Reply to ${n}`,
  agreement_reply: (n) => `Reply to ${n}`,
  call_followup: (n) => `Follow-up to ${n}`,
  nudge_due: (n) => `Nudge to ${n}`,
  final_check_due: (n) => `Check-in with ${n}`,
  wake_up_due: (n) => `Check-in with ${n}`,
  re_engage: (n) => `Check-in with ${n}`,
  pricing_stale: (n) => `Follow-up to ${n}`,
  agreement_stale: (n) => `Follow-up to ${n}`,
  no_show_rescue: (n) => `New times for ${n}`,
};

/** The phone's top-bar title, and the desktop bar's words when nothing is quoted. */
export function composerTitle(kind, name) {
  const who = firstWord(name) || "them";
  const make = TITLES[kind] || ((n) => `Email to ${n}`);
  return make(who);
}

/** The guard's noun: "your reply to Ella" for an answer, "your email to Dana" otherwise. */
export function draftNoun(kind) {
  return REPLY_KINDS.has(kind) ? "reply" : "email";
}

/*
 * HOW THE DRAFT WAS MADE. Never present a stub as a generated draft: the model's
 * words are "drafted", his own fixed email (the no-show rescue) is "your standard
 * email", and the template that stands in when the model is down says so.
 */
export function generatorLabel(draft, edited = false) {
  const d = draft || {};
  const how = d.generator === "claude" ? "drafted" : d.standard ? "your standard email" : "template, no model available";
  return edited ? `${how} · edited` : how;
}

/* ------------------------------------------------------------ what it answers */

const own = (row) => (row && (row.direction || (row.metadata && row.metadata.direction))) || null;
const isMail = (row) => row && row.subtype === "email" && !isCalendarMail(row) && !(row.metadata && row.metadata.bulk);

// "today", else "Sep 30": the quote line is a date, never a relative "yesterday"
// (frame 1d quotes Ella's Sep 30 message on Oct 1 as "Sep 30").
function dayWord(ts, now) {
  return daysBetween(ts, now) === 0 ? "today" : shortDate(ts, { now });
}

function heldBy(row, cid) {
  return cid !== null && cid !== undefined && peopleOf(row).has(String(cid));
}

function nameIn(row, cid) {
  if (!row) return null;
  if (String(row.contact_id) === String(cid) && row.contact_first_name) return row.contact_first_name;
  const h = (Array.isArray(row.holders) ? row.holders : []).find((x) => x && String(x.contact_id) === String(cid));
  return (h && (h.first_name || h.email)) || null;
}

/*
 * WHAT THIS EMAIL ANSWERS, as the composer's first line.
 *
 *   a reply     their newest message that they wrote to us ("Replying to Ella,
 *               Sep 30: Two redlines…"); the one they were only copied on is the
 *               fallback, and then the line names who wrote it
 *   a first     their form, in their words ("Replying to Dana's form, today: We
 *   email       spend about…"): the phone frame quotes it at the top
 *   a chase     our last message to them, because that is what a nudge follows
 *               up ("Following up your Sep 28 email: Hey Marco, here's…")
 *   a welcome,  nothing: they start something, and the composer is titled instead
 *   an intro
 *
 * `head` is the phone's dated line, both zones, theirs first ("Dana, form, today
 * 07:05 ET · 14:05 your time"); `text` is the whole message for the phone, `line`
 * the one-line desktop quote. Null when there is nothing to quote.
 */
export function replyQuote({ rows = [], contactId = null, kind = null, name = null, zones = null, now = new Date() } = {}) {
  if (!contactId || !Array.isArray(rows) || !rows.length) return null;
  if (kind === "kickoff" || kind === "finance_intro" || CALL_KINDS.has(kind)) return null;
  const cid = String(contactId);
  const first = firstWord(name);

  const dated = (who, source, at) => {
    const both = bothZones(at, zones || {}, {});
    return [who, source, [dayWord(at, now), both && both.text].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  };

  if (kind === "first_touch") {
    const form = latestFormSubmission(rows.filter((r) => heldBy(r, cid)));
    if (!form) return null;
    const who = nameIn(form, cid) || first || "Their";
    const text = formMessage(form) || "";
    const day = dayWord(form.occurred_at, now);
    return {
      source: "form",
      who,
      at: form.occurred_at,
      text,
      line: `Replying to ${who}'s form, ${day}${text ? `: ${firstLine(text, 90)}` : ""}`,
      head: dated(who, "form", form.occurred_at),
    };
  }

  const mail = rows.filter((r) => isMail(r) && heldBy(r, cid));
  const inbound = mail.filter((r) => own(r) === "inbound");
  // Written by them, else written to them by someone else (an intro they were copied on).
  const theirs =
    inbound.find((r) => (r.sender && r.sender.contact_id ? String(r.sender.contact_id) === cid : true)) || inbound[0] || null;
  if (theirs && (REPLY_KINDS.has(kind) || FOLLOW_KINDS.has(kind) || !kind)) {
    // A chase follows our own message when we wrote last; a reply answers theirs.
    const ours = mail.find((r) => own(r) === "outbound");
    const weWroteLast = ours && toDate(ours.occurred_at) > toDate(theirs.occurred_at);
    if (!(FOLLOW_KINDS.has(kind) && weWroteLast)) {
      const w = whoLine(theirs);
      const who = (w && w.sender) || nameIn(theirs, cid) || first || "them";
      const { body } = cleanBodyFolded(theirs.body || "");
      return {
        source: "email",
        who,
        at: theirs.occurred_at,
        text: body,
        line: `Replying to ${who}, ${dayWord(theirs.occurred_at, now)}${body ? `: ${firstLine(theirs.body, 90)}` : ""}`,
        head: dated(who, null, theirs.occurred_at),
      };
    }
  }
  if (FOLLOW_KINDS.has(kind)) {
    const ours = mail.find((r) => own(r) === "outbound");
    if (!ours) return null;
    const { body } = cleanBodyFolded(ours.body || "");
    return {
      source: "ours",
      who: "You",
      at: ours.occurred_at,
      text: body,
      line: `Following up your ${dayWord(ours.occurred_at, now) === "today" ? "email today" : `${shortDate(ours.occurred_at, { now })} email`}${body ? `: ${firstLine(ours.body, 90)}` : ""}`,
      head: dated("You", null, ours.occurred_at),
    };
  }
  return null;
}

/* ------------------------------------------------------------ offered times */

const WEEKDAYS = { Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday", Sun: "Sunday" };
// The scheduler's label, src/automations/slots.js label(): "Tue Sep 8, 11:00-11:30 (ET)".
const SLOT_LABEL = /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+([A-Z][a-z]{2})\s+(\d{1,2}),\s*(\d{1,2}:\d{2})(?:\s*[-–]\s*\d{1,2}:\d{2})?\s*\(([^)]+)\)$/;

/**
 * One offered slot as the chip prints it: "Tue Oct 6 · 10:00 ET", and as the
 * email would say it: "Tuesday Oct 6 at 10:00 ET". A label in any other shape is
 * shown as it came (zone unknown), never re-read into a guessed time.
 */
export function slotChip(label) {
  const raw = String(label || "").trim();
  const m = SLOT_LABEL.exec(raw);
  if (!m) return { label: raw, text: raw, phrase: raw, zone: null, month: null, day: null, start: null };
  const [, wd, month, day, start, zone] = m;
  return {
    label: raw,
    text: `${wd} ${month} ${day} · ${start} ${zone}`,
    phrase: `${WEEKDAYS[wd]} ${month} ${day} at ${start} ${zone}`,
    zone,
    month,
    day: Number(day),
    start,
  };
}

/*
 * THE CHIPS, OR NONE. Times are only ever offered in the lead's own zone: the
 * server already refuses to compute them for a lead it cannot place (no zone,
 * no times, src/actions/reply.js firstTouchContext), and when the host knows the
 * record's zones and theirs is unknown the composer refuses too, so a list that
 * slipped through in our zone is never printed as if it were theirs.
 */
export function offeredChips(slots, zones = undefined) {
  if (zones && typeof zones === "object" && "theirs" in zones && !zones.theirs) return [];
  const seen = new Set();
  const out = [];
  for (const s of Array.isArray(slots) ? slots : []) {
    const chip = slotChip(s);
    if (!chip.text || seen.has(chip.text)) continue;
    seen.add(chip.text);
    out.push(chip);
  }
  return out;
}

/** "ET" when every chip is in one zone, for "Offer times in ET:"; else null. */
export function chipsZone(chips) {
  const zones = [...new Set((chips || []).map((c) => c.zone).filter(Boolean))];
  return zones.length === 1 ? zones[0] : null;
}

const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/*
 * IS THIS TIME ALREADY IN THE EMAIL? The day ("Oct 6", "October 6", never "Oct
 * 16") with the start time ("10:00", or "9:00" for "09:00") a short way after
 * it. The model writes the times in its own sentence ("Tuesday Oct 6 at 10:00
 * ET"), so this reads the words rather than looking for the chip's label.
 */
export function slotInBody(chip, body) {
  const text = String(body || "");
  if (!chip) return false;
  if (!chip.month) return !!chip.label && text.includes(chip.label);
  const day = new RegExp(`\\b${escapeRe(chip.month)}[a-z]*\\.?\\s+${chip.day}(?!\\d)`, "gi");
  const [h, mm] = chip.start.split(":");
  const time = new RegExp(`(^|[^\\d])0?${Number(h)}:${mm}(?!\\d)`);
  let m;
  while ((m = day.exec(text))) {
    if (time.test(text.slice(m.index + m[0].length, m.index + m[0].length + 32))) return true;
  }
  return false;
}

/* ------------------------------------------------------------ putting words in */

/*
 * A PARAGRAPH BEFORE THE SIGN-OFF. The drafts end "…in October.\n\nGabriel", so
 * an added line (the booking link, an offered time) goes above the name rather
 * than after it. A last paragraph of a few short words is the sign-off; anything
 * longer is prose, and the line goes at the end.
 */
export function beforeSignOff(body, paragraph) {
  const raw = String(body || "");
  const add = String(paragraph || "").trim();
  if (!add) return raw;
  const text = raw.replace(/\s+$/, "");
  const tail = raw.slice(text.length);
  if (!text) return add;
  let last = null;
  const sep = /\n[ \t]*\n/g;
  let m;
  while ((m = sep.exec(text))) last = m;
  if (last) {
    const closing = text.slice(last.index + last[0].length).trim();
    if (closing.length <= 40 && closing.split(/\s+/).length <= 4) {
      return `${text.slice(0, last.index)}\n\n${add}${text.slice(last.index)}${tail}`;
    }
  }
  return `${text}\n\n${add}${tail}`;
}

/** `text` at the caret (or over the selection); the caret lands after it. */
export function insertAt(body, text, start = null, end = null) {
  const raw = String(body || "");
  const s = Number.isInteger(start) ? Math.max(0, Math.min(start, raw.length)) : raw.length;
  const e = Number.isInteger(end) ? Math.max(s, Math.min(end, raw.length)) : s;
  return { body: raw.slice(0, s) + text + raw.slice(e), caret: s + text.length };
}

/** The sentence an offered time goes into when it has no caret to land at. */
export function timeSentence(chip) {
  return `I can also do ${chip.phrase}.`;
}

/*
 * THE BOOKING LINK, in the first email and the Re-engage check-in (the brief: "the
 * first email carries the booking link"; reality.txt [17]: Re-engage is a check-in
 * with the booking link inside). The model does not know the link, so the
 * composer puts it in the starting draft as his own sentence. Only a link that
 * works: a switched-off page would greet them with "paused".
 */
export const BOOKING_SENTENCE = (url) => `You can also pick a time on my calendar: ${url}`;

export function bookingUrlOf(card) {
  return card && card.url && card.open !== false ? card.url : null;
}

export function withBookingLink(body, url) {
  if (!url || String(body || "").includes(url)) return String(body || "");
  return beforeSignOff(body, BOOKING_SENTENCE(url));
}

/*
 * Does this composer put the booking link in? When the line asked for it (Re-engage
 * passes booking_link: true), and always on a first email. A false from a host is
 * not an opt-out: the record's shell says false for every line that did not ask,
 * first emails included.
 */
export function wantsBookingLink(kind, asked = undefined) {
  return asked === true || kind === "first_touch";
}

/** The chip under the body: what happened to the booking link, in four words or so. */
export function bookingChip(card, body) {
  if (!card) return { state: "none", text: "No booking link set up" };
  if (!card.url) return { state: "none", text: "No booking link set up" };
  if (card.open === false) return { state: "off", text: "Booking page is switched off, so no link" };
  return String(body || "").includes(card.url)
    ? { state: "in", text: "Booking link · included" }
    : { state: "out", text: "Add the booking link" };
}

/* ------------------------------------------------------------ Cc and the confirm */

const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

/** A typed Cc, cleaned, or the reason it is not one. The server checks again. */
export function ccAddress(input) {
  const email = String(input || "").trim().toLowerCase();
  if (!email) return { email: null, error: null };
  if (!EMAIL.test(email)) return { email: null, error: "That isn't an email address." };
  return { email, error: null };
}

/** "Priya Raman" for an address on the company, else the address itself. */
export function ccName(email, people = []) {
  const e = String(email || "").toLowerCase();
  const p = (people || []).find((x) => x && typeof x.email === "string" && x.email.toLowerCase() === e);
  const name = p ? [p.first_name, p.last_name].filter((s) => typeof s === "string" && s.trim()).join(" ").trim() : "";
  return name || email;
}

/*
 * THE LAST QUESTION BEFORE IT GOES. The title names the person ("Send this to
 * Ella?") because the mistake worth preventing is sending it to the wrong one;
 * the line under it names every address and where it lands, because that is the
 * last moment either can matter.
 */
export function confirmWords({ name = null, to = null, cc = [], people = [], newThread = false, hasThread = false, subject = "", files = [] } = {}) {
  const who = firstWord(name) || to || "them";
  const title = `Send this to ${who}${newThread ? " as a new email" : ""}?`;
  const ccText = (cc || []).length ? ` · Cc ${(cc || []).map((e) => ccName(e, people)).join(", ")}` : "";
  const where = newThread || !hasThread
    ? `a new email${subject ? `, "${subject}"` : ""}`
    : `a reply in the thread${subject ? ` "${subject}"` : ""}`;
  // The files are named at the last moment too: "with the deck" is the other
  // thing that is easy to get wrong and impossible to take back.
  const list = (files || []).map((f) => f.name);
  const withText = list.length ? ` · with ${list.length > 2 ? `${list.length} files` : list.join(" and ")}` : "";
  return { title, detail: `To ${to || who}${ccText} · ${where}${withText}` };
}

/* ------------------------------------------------------------ attached files */

// Gmail's own ceiling on attachments. Said in the composer before the send, so a
// file too big is caught while it is still being added, not by a failed send.
export const ATTACH_LIMIT = 25 * 1024 * 1024;

/** "312 KB", "4.2 MB": a file's size as the chip says it. */
export function fileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

/*
 * Adding files to the ones already there: { files, error }. A file already on
 * the email (same name and size) is not added twice, an empty one is refused,
 * and anything that would take the email past 25 MB is left off with the reason.
 */
export function addFiles(current, incoming) {
  const out = [...(current || [])];
  let total = out.reduce((s, f) => s + (f.size || 0), 0);
  const refused = [];
  for (const f of incoming || []) {
    if (out.some((g) => g.name === f.name && g.size === f.size)) continue;
    if (!f.size) {
      refused.push(`${f.name} is empty`);
      continue;
    }
    if (total + f.size > ATTACH_LIMIT) {
      refused.push(`${f.name} would take this past 25 MB, Gmail's limit`);
      continue;
    }
    total += f.size;
    out.push(f);
  }
  return { files: out, error: refused.length ? `${refused.join("; ")}.` : null };
}

/* ------------------------------------------------------------ errors */

export const SETTINGS_LINK = "Reconnect Gmail in Settings";

/*
 * AN ERROR, BESIDE THE CONTROL THAT FAILED, saying the draft is safe (frame 1i:
 * "Gmail permission is missing. Reconnect Gmail in Settings, your draft is
 * kept."). `link` is the phrase in `text` that goes to Settings, or null.
 */
export function errorWords(message, what = "send") {
  const msg = String(message || "").trim().replace(/[.\s]+$/, "");
  if (/insufficient|scope|permission|forbidden|\b403\b/i.test(msg)) {
    return { text: `Gmail permission is missing. ${SETTINGS_LINK}, your draft is kept.`, link: SETTINGS_LINK };
  }
  if (/not connected|no gmail|invalid_grant|token/i.test(msg)) {
    return { text: `Gmail isn't connected. ${SETTINGS_LINK}, your draft is kept.`, link: SETTINGS_LINK };
  }
  if (what === "copy") return { text: "Could not copy. Select the text and copy it by hand.", link: null };
  const verb = what === "save" ? "Could not save the draft" : what === "draft" ? "Could not draft this" : "Could not send";
  // The server already says "could not draft this: …" for a model that is down;
  // the reason is kept, the words are not said twice.
  const reason = msg.toLowerCase().startsWith(verb.toLowerCase()) ? msg.slice(verb.length).replace(/^[:\s]+/, "") : msg;
  // A draft that never arrived has nothing to keep; a send or a save that failed
  // still has every word he wrote in front of him.
  return { text: `${verb}${reason ? `: ${reason}` : ""}.${what === "draft" ? "" : " Your draft is kept."}`, link: null };
}

/* ------------------------------------------------------------ the unsent-draft registry */

/*
 * ASKING BEFORE A CLOSE THAT IS NOT THE COMPOSER'S OWN.
 *
 * The composer can guard its own × and its own Esc layer. It cannot guard the
 * panel around it: the host's × unmounts it, and an unmount cannot be refused.
 * So while a composer holds edits it registers here with its DOM node and how to
 * ask, and whatever is about to close a container (the record's panel, its
 * phone sheet) calls ask(proceed, container) first:
 *
 *   if (!unsent.ask(onClose, rootEl)) onClose();
 *
 * True means an edited composer inside that container stopped the close and is
 * asking ("Throw away your reply to Ella?"); it calls proceed() itself once he
 * has chosen Discard, or once Save to Gmail has filed the draft. Keep editing
 * calls nothing. A composer OUTSIDE the container (Today's inline draft beside
 * the record) is not asked about a close that does not touch it.
 *
 * Pure apart from the one shared instance at the bottom, the way escape.mjs is.
 */
export function createUnsent() {
  const held = [];
  return {
    /** Registers an edited composer. Returns the function that releases it. */
    hold(node, ask) {
      const entry = { node, ask };
      held.push(entry);
      return () => {
        const i = held.indexOf(entry);
        if (i >= 0) held.splice(i, 1);
      };
    },
    /** True when an edited composer inside `container` stopped the close to ask. */
    ask(proceed, container = null) {
      for (let i = held.length - 1; i >= 0; i -= 1) {
        const e = held[i];
        const inside = !container || (e.node && typeof container.contains === "function" && container.contains(e.node));
        if (inside) {
          e.ask(typeof proceed === "function" ? proceed : () => {});
          return true;
        }
      }
      return false;
    },
    /** Is an edited composer open inside `container` (or anywhere)? */
    holds(container = null) {
      return held.some((e) => !container || (e.node && typeof container.contains === "function" && container.contains(e.node)));
    },
    get size() {
      return held.length;
    },
  };
}

// The app's one registry.
export const unsent = createUnsent();
